import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Send, FileText, Check, CheckCheck, AlertCircle, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import WhatsAppTemplatePicker from "./WhatsAppTemplatePicker";
import WaText from "./WaText";

function StatusIcon({ status }) {
  if (status === "sent") return <Check className="inline h-3 w-3 text-muted-foreground" />;
  if (status === "delivered") return <CheckCheck className="inline h-3 w-3 text-muted-foreground" />;
  if (status === "read") return <CheckCheck className="inline h-3 w-3 text-blue-500" />;
  if (status === "failed") return <AlertCircle className="inline h-3 w-3 text-red-500" />;
  return <Clock className="inline h-3 w-3 text-muted-foreground" />;
}

function ProviderTag({ provider }) {
  if (!provider) return null;
  return (
    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{provider}</span>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// `blocked`: the lead is blacklisted — no sending (history stays visible).
export default function WhatsAppChat({ leadId, lead, blocked = false }) {
  const [messages, setMessages] = useState([]);
  const [lastInboundAt, setLastInboundAt] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const bottomRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/wa/leads/${leadId}/messages`);
      setMessages(data.messages || []);
      setLastInboundAt(data.last_inbound_at || null);
    } catch {
      // silent — will show empty chat
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Load the WhatsApp numbers this user may send from (across both providers)
  // plus a suggested default; the picker only shows when >1 sender.
  useEffect(() => {
    api.get("/wa/senders", { params: { lead_id: leadId } })
      .then(({ data }) => {
        const list = data.senders || [];
        setSenders(list);
        const def = data.default
          ? list.find((s) => s.provider === data.default.provider && s.account_id === data.default.account_id)
          : null;
        const chosen = def || list[0] || null;
        setSelectedSenderId(chosen ? `${chosen.provider}:${chosen.account_id}` : "");
      })
      .catch(() => { setSenders([]); setSelectedSenderId(""); });
  }, [leadId]);

  useLiveEvents((event) => {
    if ((event.type === "whatsapp_message" || event.type === "whatsapp_status") && event.lead_id === leadId) {
      loadMessages();
    }
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const inWindow = lastInboundAt && (Date.now() - new Date(lastInboundAt).getTime()) < 24 * 60 * 60 * 1000;
  const sel = senders.find((s) => `${s.provider}:${s.account_id}` === selectedSenderId) || null;
  const noSender = senders.length === 0;

  const sendText = async () => {
    const trimmed = text.trim();
    if (!trimmed || !sel) return;
    setSending(true);
    try {
      await api.post("/wa/send", {
        provider: sel.provider,
        account_id: sel.account_id,
        lead_id: leadId,
        text: trimmed,
      });
      setText("");
      await loadMessages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSent = () => {
    setTemplateOpen(false);
    loadMessages();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Loading messages...</div>;
  }

  return (
    <div className="flex h-[500px] flex-col">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto space-y-2 p-3 bg-cream/30 rounded-lg border border-navy/5">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            No WhatsApp messages yet. Send a template to start a conversation.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
              m.direction === "out"
                ? "bg-green-100 text-green-900"
                : "bg-white text-navy border border-navy/10"
            }`}>
              {m.msg_type === "template" ? (
                <div className="italic text-xs text-muted-foreground mb-1">Template</div>
              ) : null}
              <WaText text={m.body} />
              <div className="flex items-center justify-end gap-1.5 mt-1">
                <ProviderTag provider={m.provider} />
                <span className="text-[10px] text-muted-foreground">{formatTime(m.timestamp)}</span>
                {m.direction === "out" && <StatusIcon status={m.status} />}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Sender picker — only when the user can send from multiple numbers */}
      {senders.length > 1 && (
        <div className="mt-3">
          <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Send from…" /></SelectTrigger>
            <SelectContent>
              {senders.map((s) => (
                <SelectItem key={`${s.provider}:${s.account_id}`} value={`${s.provider}:${s.account_id}`}>
                  {s.label}{s.number ? ` (${s.number})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Input area */}
      {blocked ? (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="whatsapp-blocked-note">Lead is blacklisted — messaging is off.</p>
      ) : (
      <div className="mt-3 flex gap-2 items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={() => setTemplateOpen(true)} disabled={noSender} className="shrink-0">
                <FileText className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Send Template</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex-1">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                  placeholder={noSender ? "No WhatsApp number configured" : (inWindow ? "Type a message..." : "Send a template to start a conversation")}
                  disabled={noSender || !inWindow || sending}
                />
              </div>
            </TooltipTrigger>
            {(noSender || !inWindow) && (
              <TooltipContent>
                {noSender
                  ? "No WhatsApp number configured"
                  : "Freeform text is only allowed within 24 hours of the customer's last message. Use a template instead."}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <Button
          onClick={sendText}
          disabled={noSender || !inWindow || !text.trim() || sending}
          className="bg-green-600 text-white hover:bg-green-700 shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      )}

      <WhatsAppTemplatePicker
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        leadId={leadId}
        lead={lead}
        sender={sel}
        onSent={handleTemplateSent}
      />
    </div>
  );
}
